import React from 'react';

import { Box } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useSelector } from 'react-redux';
import {
  ALLOWED_STEPS_REVIEW_IN_PROGRESS,
  ALLOWED_STEPS_REVIEW_PUBLISHED,
  REVIEW_CYCLE_STEPS_METADATA,
  REVIEW_CYCLE_STEPS_ORDER,
  REVIEW_CYCLE_STEPS_V1,
  REVIEW_FORM_STAGE,
  useReviewCreationData,
} from 'reducers/reviewCreationSlice';
import { sidebarNavIsExpandedSelector } from 'selectors/sidebarNav';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { SectionFooter } from 'uiToolkitV1/SectionFooter/SectionFooter';

import { useReviewCreationNavigation } from './Hooks/useReviewCreationNavigation';

const isBackStepAllowed = ({ step, currentStage }) => {
  // filter validation step if not in draft stage
  const validReviewCycleSteps =
    currentStage === REVIEW_FORM_STAGE.DRAFT
      ? REVIEW_CYCLE_STEPS_ORDER
      : REVIEW_CYCLE_STEPS_ORDER.filter((step) => step !== REVIEW_CYCLE_STEPS_V1.VALIDATION);

  const currentIndex = validReviewCycleSteps.indexOf(step);
  if (currentIndex === 0) {
    return false;
  }

  const prevStep = validReviewCycleSteps[currentIndex - 1];

  if (currentStage === REVIEW_FORM_STAGE.IN_PROGRESS) {
    return ALLOWED_STEPS_REVIEW_IN_PROGRESS.includes(prevStep);
  }
  if (currentStage === REVIEW_FORM_STAGE.PUBLISHED) {
    return ALLOWED_STEPS_REVIEW_PUBLISHED.includes(prevStep);
  }
  return true;
};

const getNextStep = ({ currentStep, currentStage }) => {
  // filter validation step if not in draft stage
  const validReviewCycleSteps =
    currentStage === REVIEW_FORM_STAGE.DRAFT
      ? REVIEW_CYCLE_STEPS_ORDER
      : REVIEW_CYCLE_STEPS_ORDER.filter((step) => step !== REVIEW_CYCLE_STEPS_V1.VALIDATION);

  const currentIndex = validReviewCycleSteps.indexOf(currentStep);
  if (currentIndex === validReviewCycleSteps.length - 1) {
    return null;
  }
  return validReviewCycleSteps[currentIndex + 1];
};

export const getPrevStep = ({ currentStep, currentStage }) => {
  // filter validation step if not in draft stage
  const validReviewCycleSteps =
    currentStage === REVIEW_FORM_STAGE.DRAFT
      ? REVIEW_CYCLE_STEPS_ORDER
      : REVIEW_CYCLE_STEPS_ORDER.filter((step) => step !== REVIEW_CYCLE_STEPS_V1.VALIDATION);
  const currentIndex = validReviewCycleSteps.indexOf(currentStep);
  if (currentIndex === 0) {
    return null;
  }
  return validReviewCycleSteps[currentIndex - 1];
};

const ReviewCreationFooter = ({ isContinueDisabled, onContinue, rightContent = null }) => {
  const { step, currentStage } = useReviewCreationData();
  const { goToPrevStep } = useReviewCreationNavigation();
  const isL2NavVisible = useSelectIsL2Visible();

  const nextStep = getNextStep({ currentStep: step, currentStage });
  const prevStep = getPrevStep({ currentStep: step, currentStage });

  const isExpandedState = useSelector(sidebarNavIsExpandedSelector);
  const leftRabbitUI = isExpandedState ? '242px' : '16px';
  const leftDeerUI = isL2NavVisible ? '320px' : '80px';

  const isBackAllowed = isBackStepAllowed({ step, currentStage });
  return (
    <Box
      position="fixed"
      bottom={0}
      left={checkNewNavEnabled() ? leftDeerUI : leftRabbitUI}
      right={0}
      transition="left 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
    >
      <SectionFooter
        backText={`Back To ${prevStep ? REVIEW_CYCLE_STEPS_METADATA[prevStep]?.title : ''}`}
        continueText={`To ${nextStep ? REVIEW_CYCLE_STEPS_METADATA[nextStep]?.title : ''}`}
        onBack={isBackAllowed ? goToPrevStep : null}
        onContinue={onContinue}
        isContinueDisabled={isContinueDisabled}
        rightContent={rightContent}
      />
    </Box>
  );
};

export { ReviewCreationFooter };
